import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { HistoryPanel, type HistoryItem } from './HistoryPanel';
import { perfLog } from '../perfLog';
export type { HistoryItem };

// TikTok-style vertical iframe stack. The first iframe loads `initialUrl`; each
// time the currently-visible game dispatches the configured "loaded" event
// (on document, or via window.postMessage from a cross-origin iframe), the
// next URL is appended below. A vertical swipe gesture snaps between frames.

/**
 * Imperative handle exposed via `ref`. Consumers (e.g. SlotUI's spin button)
 * can call `postToActive` to deliver a `ui.*` message to the currently-visible
 * game iframe without needing to know which index is active or how the stack
 * addresses its children.
 */
export interface ReelsStackHandle {
  /**
   * Post a message to the iframe that is currently visible. No-op if the
   * active iframe hasn't finished navigating yet. Target origin is `'*'`
   * because reel iframes are cross-origin (different ports count).
   */
  postToActive: (type: string, payload?: unknown) => void;
  /** Index of the currently-visible reel (same value passed to `onIndexChange`). */
  getActiveIndex: () => number;
  /**
   * Returns true when the given value is the `contentWindow` of the currently
   * visible iframe. Used by the shell to filter inbound `MessageEvent`s so it
   * only reacts to messages from the iframe that's actually on-screen (offscreen
   * reels are paused but technically still alive and could post).
   */
  isActiveSource: (source: MessageEventSource | null) => boolean;
}

export interface ReelsStackProps {
  /** URL of the first iframe (e.g. http://localhost:3000/#remoteUI). */
  initialUrl: string;
  /**
   * Factory that produces the URL for each subsequent iframe.
   * `index === 0` is the *second* frame (first one past `initialUrl`).
   * Default: `http://localhost:${3001 + index}/#remoteUI`.
   */
  nextUrlFactory?: (index: number) => string;
  /**
   * Name of the custom event the hosted game dispatches when it finishes loading.
   * Triggers creation of the next iframe. Also accepted via `window.postMessage`
   * as either the raw string or `{ type: <name> }`, so cross-origin iframes work.
   */
  loadedEventName?: string;
  /** Optional iframe sandbox attribute. */
  sandbox?: string;
  /** Optional iframe allow attribute. */
  allow?: string;
  /** Notifies the host when the visible frame changes. */
  onIndexChange?: (index: number) => void;
  /** Ratio of container height the user must drag past for a snap to trigger. */
  snapThreshold?: number;
  /**
   * Message payload sent to an iframe when it becomes inactive. Off-screen
   * games should stop driving their render loop. Default: `'game.pauseRendering'`.
   */
  pauseMessage?: string;
  /** Message payload sent to an iframe when it becomes active. Default: `'game.resumeRendering'`. */
  resumeMessage?: string;
  /**
   * Message type sent to an iframe that is about to be blanked (≥ 2 positions behind the
   * active index). The game uses this to tear down its WebGL context, audio, and DOM nodes
   * before the src is set to `about:blank`, preventing Safari's WebGL context limit from
   * being exceeded after the third reel. Default: `'game.destroy'`.
   */
  destroyMessage?: string;
  /**
   * Called when the status of the immediate next reel changes. Possible states:
   *   - `'none'`    — there is no next reel queued (end of stack)
   *   - `'loading'` — next reel exists but hasn't emitted `game.loaded` yet
   *   - `'ready'`   — next reel has emitted `game.loaded` and is swipe-ready
   */
  onNextLoadingChange?: (info: { status: 'none' | 'loading' | 'ready'; nextIndex: number; progress: number }) => void;
  /**
   * Name of the load-progress message the hosted game posts while assets are
   * downloading. Expected shape: `{ type: progressMessageName, progress: number }`
   * where `progress` is 0–100. Default: `'game.loadProgress'`.
   */
  progressMessageName?: string;
  /**
   * Fires during pointer drag with the current vertical offset in px and
   * whether a drag is in progress. Consumers use this to parallax chrome
   * (e.g. the bottom panel) along with the moving reel stack.
   */
  onDragChange?: (info: { offset: number; dragging: boolean }) => void;
  /**
   * Name of the title-broadcast message the hosted game uses. Each game
   * posts `{ type: titleMessageName, title: document.title }` when its
   * title is first set and whenever it changes. Default: `'game.title'`.
   */
  titleMessageName?: string;
  /**
   * Called when the active iframe's `document.title` becomes known or
   * changes. Receives `null` before any title has been reported for the
   * active reel. Hosts typically use this to override their own title chrome.
   */
  onActiveTitleChange?: (title: string | null) => void;
  /**
   * When true, force-pause the active iframe even though it would normally
   * be the resumed one. Used by SlotUI to keep the game paused while a
   * modal sheet is open over it (bet picker, autospin picker, not-enough-
   * money popup). Off-screen iframes are paused regardless. The pause/
   * resume effect re-fires every time this flips, so a preload that
   * appends a URL while a modal is open won't accidentally un-pause
   * the active reel.
   */
  paused?: boolean;
  /**
   * When true, pointer-driven swipe gestures are ignored entirely — no drag
   * tracking starts at all. Use this to lock the stack while the active
   * game is spinning so players can't swipe mid-spin. This also blocks the
   * swipe-down-for-history gesture, so keep it narrow (spin / disabled-
   * input states) rather than reaching for it for anything more transient —
   * see `nextDisabled` for blocking just the "advance to next game" swipe.
   */
  swipeDisabled?: boolean;
  /**
   * When true, an upward swipe past the "next game" threshold does nothing
   * instead of advancing — used while autoplay is queued up or a modal
   * (bet picker, autospin picker, insufficient-funds popup) is open, so a
   * stray swipe can't skip games out from under those states. Unlike
   * `swipeDisabled`, this leaves the downward swipe-for-history gesture
   * untouched, since there's no reason to also lock players out of their
   * spin history just because autoplay happens to be running.
   */
  nextDisabled?: boolean;
  /**
   * When true, the active iframe becomes pointer-interactive so direct
   * taps land inside the hosted game (e.g. a bonus round rendered by the
   * game itself). Every iframe is `pointer-events: none` by default —
   * intentionally, so the wrapping div's own pointer handlers can track
   * the swipe gesture without the cross-origin iframe swallowing the
   * input first. Only the currently-active iframe is ever made
   * interactive; off-screen ones stay non-interactive regardless.
   * Default: `false`.
   */
  interactive?: boolean;
  /** Called when the history panel opens or closes. */
  onHistoryOpenChange?: (open: boolean) => void;
}

// Module-level helpers — defined outside the component so they don't allocate
// on every render.

// Strip `autoplay` from the iframe `allow` attribute. Prefetched reels would
// otherwise be allowed to create AudioContexts before the user has ever seen
// them; multiple stacked AudioContexts crash iOS Safari. Audio still works
// after the user interacts with the iframe (the swipe + spin click both
// satisfy WebKit's user-activation gate inside the frame's own origin), so
// dropping the grant is a no-op for legit audio and a hard fix for iOS.
function sanitizeAllow(allow: string | undefined): string | undefined {
  if (!allow) return allow;
  return allow
    .split(';')
    .map((s) => s.trim())
    .filter((s) => s && !/^autoplay\b/i.test(s))
    .join('; ');
}

// Hard cap on the visitedGames history. Each entry holds a title, URL,
// timestamp and spin count — small individually, but over a long session a
// player could rack up hundreds. The history panel is the only consumer
// and it never displays more than the most recent ~50.
const MAX_VISITED_GAMES = 50;

// Lightweight dev-only log — drops out of production bundles when Vite
// inlines `import.meta.env.DEV` as `false`, so the logged objects can be
// GC'd instead of being retained by console.log's argument capture.
const devLog = (...args: unknown[]) => {
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};
const devWarn = (...args: unknown[]) => {
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};

export const ReelsStack = forwardRef<ReelsStackHandle, ReelsStackProps>(function ReelsStack({
  initialUrl,
  nextUrlFactory = (i) => `http://localhost:${3001 + i}/#remoteUI`,
  loadedEventName = 'game.loaded',
  sandbox,
  allow,
  onIndexChange,
  snapThreshold = 0.18,
  pauseMessage = 'game.pauseRendering',
  resumeMessage = 'game.resumeRendering',
  destroyMessage = 'game.destroy',
  onNextLoadingChange,
  onDragChange,
  titleMessageName = 'game.title',
  progressMessageName = 'game.loadProgress',
  onActiveTitleChange,
  paused = false,
  swipeDisabled = false,
  nextDisabled = false,
  interactive = false,
  onHistoryOpenChange,
}, ref) {
  // Same `allow` for every iframe (no autoplay), regardless of active status.
  // We can't tighten it on prefetch and loosen it on activation without
  // remounting the iframe (and losing the prefetch), so we apply the safer
  // policy uniformly.
  const safeAllow = sanitizeAllow(allow);
  const [urls, setUrls] = useState<string[]>([initialUrl]);
  const [index, setIndex] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Negative px offset applied to the history overlay while the user is
  // swiping it upward to dismiss. Driven by callbacks from HistoryPanel.
  const [dismissProgress, setDismissProgress] = useState(0);
  // URL of a history-item that is currently loading. Null when idle.
  const [loadingHistoryUrl, setLoadingHistoryUrl] = useState<string | null>(null);
  // Title of that same history item — shown on the full-screen loading
  // overlay below so it reads "Loading Save the Dead 2" rather than just
  // an anonymous spinner. Kept separate from `loadingHistoryUrl` (rather
  // than storing the whole HistoryItem) since the url is the only part
  // anything else in this component actually keys off of.
  const [loadingHistoryTitle, setLoadingHistoryTitle] = useState<string | null>(null);
  // Indices of iframes we've heard a game.loaded signal from. Used to compute
  // the "next reel is preloading" state for the bottom-panel indicator.
  const [loadedSet, setLoadedSet] = useState<Set<number>>(() => new Set());
  // Load progress (0-100) per iframe index, reported via game.loadProgress postMessages.
  const [loadProgressMap, setLoadProgressMap] = useState<Record<number, number>>({});
  // document.title of each iframe, keyed by its stack index. Populated from
  // 'game.title' postMessages so we can show the active iframe's real name
  // in the parent UI (iframes are cross-origin so contentDocument is blocked).
  const [titles, setTitles] = useState<Record<number, string>>({});
  // Games the user has swiped through, newest first. Recorded each time the
  // user swipes forward — captures the title and URL of the departing reel.
  const [visitedGames, setVisitedGames] = useState<HistoryItem[]>([]);
  // Incremented each time the user navigates via history so index-0's iframe
  // key changes, forcing a remount even when the URL is identical to what
  // was there before (React skips DOM src updates for same-key same-prop nodes,
  // but destroyIframe blanked the src directly on the DOM element).
  const [reloadKey, setReloadKey] = useState(0);
  // Indices whose iframe has been torn down and should be removed from the
  // React tree. This is what actually caps mounted iframes on iOS: blanking
  // src releases the WebGL context, but until the `<iframe>` element itself
  // is unmounted, WebKit holds onto the page, document, and any audio it
  // may have spawned. Past reels accumulate as composited layers + retained
  // documents — three or four is enough to hit Safari's context cap and
  // crash the tab. Filling this set excludes those indices from the render.
  const [unmountedSet, setUnmountedSet] = useState<Set<number>>(() => new Set());

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pointerStartY = useRef<number | null>(null);
  const pointerId = useRef<number | null>(null);
  // One ref per iframe so we can postMessage directly to that specific
  // contentWindow when it scrolls in/out of view.
  const iframeRefs = useRef<Array<HTMLIFrameElement | null>>([]);
  // Indices we've already perf-logged element-creation for — see the
  // iframe `ref` callback below. Temporary, part of the load-gap investigation.
  const loggedIframeIndicesRef = useRef<Set<number>>(new Set());
  // Mirror of `index` in a ref so the imperative handle always sees the
  // latest value even when captured by a stable consumer closure.
  const indexRef = useRef(index);
  indexRef.current = index;

  // Imperative API exposed via `ref`. SlotUI's spin/bet/auto buttons reach
  // in here to talk to whichever iframe is currently on-screen, instead of
  // plumbing iframe refs all the way back up to App.tsx.
  useImperativeHandle(
    ref,
    () => ({
      postToActive: (type, payload) => {
        const active = iframeRefs.current[indexRef.current];
        const win = active?.contentWindow;
        if (!win) {
          devWarn('[ReelsStack] postToActive: no active iframe', {
            index: indexRef.current,
            type,
          });
          return;
        }
        devLog('[ReelsStack] → postToActive', {
          index: indexRef.current,
          type,
          payload,
        });
        try {
          win.postMessage({ type, payload }, '*');
        } catch (err) {
          devWarn('[ReelsStack] postToActive failed', type, err);
        }
      },
      getActiveIndex: () => indexRef.current,
      isActiveSource: (source) => {
        if (!source) return false;
        const active = iframeRefs.current[indexRef.current];
        return active?.contentWindow === source;
      },
    }),
    [],
  );

  // Append the next URL — but only if there isn't already a preloaded
  // reel queued past the currently-active one. This caps the stack at
  // active + 1, so we get a "swipe → load next" pattern instead of an
  // unbounded cascade where every iframe's `game.loaded` would queue
  // another. The first preload kicks off when the active iframe
  // (typically index 0 at startup) emits `game.loaded`; further
  // appends are driven by index changes (see the swipe-preload effect
  // further down).
  const appendNext = useCallback(() => {
    setUrls((prev) => {
      const activeIdx = indexRef.current;
      if (prev.length > activeIdx + 1) return prev; // preload already queued
      // index passed to factory: 0 = second frame, 1 = third, …
      const nextIdx = prev.length - 1;
      const nextUrl = nextUrlFactory(nextIdx);
      if (!nextUrl) return prev;
      devLog('[ReelsStack] appending next reel:', nextUrl);
      return [...prev, nextUrl];
    });
  }, [nextUrlFactory]);

  useEffect(() => {
    // Accept a few event name variants so callers / games don't have to agree
    // exactly on casing: 'game.loaded', 'GAME_LOADED', whatever is configured.
    const variants = new Set(
      [loadedEventName, 'game.loaded', 'GAME_LOADED', 'gameLoaded']
        .map((s) => String(s).toLowerCase()),
    );
    const matches = (raw: unknown): boolean => {
      if (typeof raw === 'string') return variants.has(raw.toLowerCase());
      if (raw && typeof raw === 'object') {
        const t = (raw as { type?: unknown }).type;
        if (typeof t === 'string') return variants.has(t.toLowerCase());
      }
      return false;
    };

    const onDocEvent = () => appendNext();
    // Attach to every variant on the document too, in case the game dispatches
    // under a slightly different name.
    variants.forEach((v) => document.addEventListener(v, onDocEvent));

    // postMessage fallback for cross-origin iframes (different ports count as
    // cross-origin, so games running on :3000/:3001 can't touch the parent's
    // document directly — they have to postMessage).
    const onMessage = (e: MessageEvent) => {
      // Title broadcast from an iframe — store by sender index.
      const d = e.data;
      if (
        d && typeof d === 'object'
        && (d as { type?: unknown }).type === titleMessageName
        && typeof (d as { title?: unknown }).title === 'string'
      ) {
        const idx = iframeRefs.current.findIndex((el) => el?.contentWindow === e.source);
        if (idx >= 0) {
          const title = (d as { title: string }).title;
          setTitles((prev) => (prev[idx] === title ? prev : { ...prev, [idx]: title }));
        }
        return;
      }

      // Load progress from a preloading iframe.
      if (
        d && typeof d === 'object'
        && (d as { type?: unknown }).type === progressMessageName
        && typeof (d as { progress?: unknown }).progress === 'number'
      ) {
        const idx = iframeRefs.current.findIndex((el) => el?.contentWindow === e.source);
        if (idx >= 0) {
          const pct = Math.min(100, Math.max(0, (d as { progress: number }).progress));
          setLoadProgressMap((prev) => prev[idx] === pct ? prev : { ...prev, [idx]: pct });
        }
        return;
      }

      // Count spins per iframe for the history panel.
      if (
        d && typeof d === 'object'
        && (d as { type?: unknown }).type === 'game.spinStart'
      ) {
        const idx = iframeRefs.current.findIndex((el) => el?.contentWindow === e.source);
        if (idx >= 0) {
          spinsPerIndexRef.current[idx] = (spinsPerIndexRef.current[idx] ?? 0) + 1;
        }
        return;
      }

      // Diagnostic-only signals from Application.ts's initializeDiagnostics()
      // (iOS black-screen investigation) — just log tagged by iframe index,
      // no state change. 'game.contextLost'/'game.contextRestored' tell us
      // whether the canvas actually lost its GPU context; 'game.error' is a
      // JS exception/unhandled rejection that could kill the render loop
      // without ever touching WebGL. If a reel goes black with none of
      // these logged, that points at iOS killing the WebContent process
      // outright — check Xcode device logs / on-device Analytics Data for
      // "WebContent" crashes.
      if (
        d && typeof d === 'object'
        && typeof (d as { type?: unknown }).type === 'string'
        && ['game.contextLost', 'game.contextRestored', 'game.error'].includes((d as { type: string }).type)
      ) {
        const idx = iframeRefs.current.findIndex((el) => el?.contentWindow === e.source);
        const { type, ...detail } = d as { type: string; [key: string]: unknown };
        const logFn = type === 'game.contextLost' || type === 'game.error' ? console.error : console.warn;
        logFn(`[ReelsStack] iframe #${idx} ${type}`, detail);
        return;
      }

      if (matches(e.data)) {
        devLog('[ReelsStack] received loaded message:', e.data);
        // Identify which iframe fired this so we know which one is now loaded.
        const idx = iframeRefs.current.findIndex((el) => el?.contentWindow === e.source);
        if (idx >= 0) {
          setLoadedSet((prev) => {
            if (prev.has(idx)) return prev;
            const next = new Set(prev);
            next.add(idx);
            return next;
          });
          // Snap progress to 100 when fully loaded.
          setLoadProgressMap((prev) => prev[idx] === 100 ? prev : { ...prev, [idx]: 100 });
        }
        appendNext();
      }
    };
    window.addEventListener('message', onMessage);

    return () => {
      variants.forEach((v) => document.removeEventListener(v, onDocEvent));
      window.removeEventListener('message', onMessage);
    };
  }, [loadedEventName, appendNext, titleMessageName]);

  // Propagate the active iframe's title upward whenever it arrives or changes.
  useEffect(() => {
    onActiveTitleChange?.(titles[index] ?? null);
  }, [titles, index, onActiveTitleChange]);

  // Refs that let window-level gesture closures always read the latest values
  // without needing to re-register listeners on every render.
  const loadedSetRef = useRef(loadedSet);
  loadedSetRef.current = loadedSet;
  const historyOpenRef = useRef(historyOpen);
  historyOpenRef.current = historyOpen;
  // History closes immediately on item click (see `handleHistoryItemClick`)
  // rather than staying open until the newly selected game loads, so
  // `historyOpenRef` alone no longer covers that whole window — this ref
  // additionally blocks the gesture for as long as that load is in flight
  // (matches the full-screen blocking overlay rendered near the bottom of
  // this component).
  const loadingHistoryUrlRef = useRef(loadingHistoryUrl);
  loadingHistoryUrlRef.current = loadingHistoryUrl;
  const swipeDisabledRef = useRef(swipeDisabled);
  swipeDisabledRef.current = swipeDisabled;
  const nextDisabledRef = useRef(nextDisabled);
  nextDisabledRef.current = nextDisabled;
  const snapThresholdRef = useRef(snapThreshold);
  snapThresholdRef.current = snapThreshold;
  const onDragChangeRef = useRef(onDragChange);
  onDragChangeRef.current = onDragChange;
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;
  const onHistoryOpenChangeRef = useRef(onHistoryOpenChange);
  onHistoryOpenChangeRef.current = onHistoryOpenChange;
  // Stable reference for HistoryPanel's onClose — was an inline arrow
  // recreated on every render (including every drag-gesture rAF frame),
  // which would defeat memoizing HistoryPanel even after fixing
  // `currentItem`'s identity above.
  const handleCloseHistory = useCallback(() => {
    setDismissProgress(0);
    setHistoryOpen(false);
    onHistoryOpenChangeRef.current?.(false);
  }, []);

  const destroyedRef = useRef<Set<number>>(new Set());
  // Incremented whenever the stack is fully reset (history navigation). Any
  // pending destroyIframe setTimeout that fires after a reset should be a
  // no-op, so we stamp each timeout with the generation at call time and
  // skip the blank if the generation has since changed.
  const destroyGenRef = useRef(0);

  // Refs that destroyIframe and the drag handlers read. They have to be
  // declared above destroyIframe (which references spinsPerIndexRef in its
  // unmount-phase setTimeout) and above the gesture useEffect (which reads
  // urlsRef inside its pointermove closure).
  const titlesRef = useRef(titles);
  titlesRef.current = titles;
  const urlsRef = useRef(urls);
  urlsRef.current = urls;
  // Spin count per iframe index — incremented by game.spinStart messages.
  const spinsPerIndexRef = useRef<Record<number, number>>({});
  // First-seen timestamp per iframe index, for the history panel's
  // "currently playing" entry. Stamped lazily the first time an index is
  // read (below) rather than via `Date.now()` inline at render time — a
  // fresh timestamp on every render would both misrepresent "when did
  // this reel become active" (it's supposed to be fixed, like every past
  // entry's timestamp) and defeat memoizing the `currentItem` object
  // passed to `HistoryPanel`.
  const activatedAtRef = useRef<Record<number, number>>({});
  const getActivatedAt = (i: number): number => {
    const existing = activatedAtRef.current[i];
    if (existing != null) return existing;
    const now = Date.now();
    activatedAtRef.current[i] = now;
    return now;
  };

  // Two-phase teardown:
  //   Phase 1 (immediate): postMessage(destroy) — gives the game a chance to
  //     dispose its WebGL context, audio graph, and timers before its document
  //     is torn down. Without this, Safari leaks the GL context for a while
  //     even after blanking, and stacked-up contexts cause page crashes.
  //   Phase 2 (~400ms later): blank src — releases the GL context. Safari
  //     sometimes won't reclaim the context purely from React unmounting;
  //     navigating to about:blank is the reliable signal.
  //   Phase 3 (~800ms later): mark for unmount — the iframe element is
  //     removed from the DOM entirely, letting the document GC happen and
  //     freeing the page's compositor layers + retained audio sessions.
  //   Phase 4 (same step): prune the side-tables (titles, loadedSet,
  //     spinsPerIndex) that were keyed by this iframe's index. Without this,
  //     they grow unbounded over a long session.
  const destroyIframe = useCallback((i: number) => {
    if (destroyedRef.current.has(i)) return;
    destroyedRef.current.add(i);
    const iframe = iframeRefs.current[i];
    if (!iframe) return;
    try { iframe.contentWindow?.postMessage({ type: destroyMessage }, '*'); } catch { /* noop */ }
    const gen = destroyGenRef.current;
    setTimeout(() => {
      if (destroyGenRef.current !== gen) return; // stack was reset; skip
      const el = iframeRefs.current[i];
      if (el) el.src = 'about:blank';
    }, 400);
    setTimeout(() => {
      if (destroyGenRef.current !== gen) return; // stack was reset; skip
      setUnmountedSet((prev) => {
        if (prev.has(i)) return prev;
        const next = new Set(prev);
        next.add(i);
        return next;
      });
      // Side-table cleanup so per-index records don't outlive the iframe.
      setLoadedSet((prev) => {
        if (!prev.has(i)) return prev;
        const next = new Set(prev);
        next.delete(i);
        return next;
      });
      setTitles((prev) => {
        if (!(i in prev)) return prev;
        const next = { ...prev };
        delete next[i];
        return next;
      });
      if (i in spinsPerIndexRef.current) {
        delete spinsPerIndexRef.current[i];
      }
      if (i in activatedAtRef.current) {
        delete activatedAtRef.current[i];
      }
    }, 800);
  }, [destroyMessage]);

  // Swipe / drag handling — window-level so gestures work over chrome elements.
  //
  // pointermove fires at the pointer's polling rate, which on iOS Safari can
  // exceed 120Hz on ProMotion devices. Each call here was running setDragY +
  // an onDragChange callback (which itself re-renders the BalanceBar parallax
  // hint) — that's a full React tree render per move event, enough to starve
  // the main thread and contend with the iframe game on lower-end iPhones.
  // We coalesce moves: stash the latest dy in a ref, schedule one rAF, and
  // flush the React state from inside that rAF. Visually identical (we still
  // hit one update per frame); ~10x less work.
  const pendingDragYRef = useRef<number | null>(null);
  const dragRafRef = useRef<number | null>(null);

  const flushDrag = useCallback(() => {
    dragRafRef.current = null;
    const v = pendingDragYRef.current;
    if (v == null) return;
    pendingDragYRef.current = null;
    setDragY(v);
    onDragChangeRef.current?.({ offset: v, dragging: true });
  }, []);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (historyOpenRef.current) return;
      if (loadingHistoryUrlRef.current) return;
      if (swipeDisabledRef.current) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      pointerStartY.current = e.clientY;
      pointerId.current = e.pointerId;
      setDragging(true);
      setDragY(0);
      onDragChangeRef.current?.({ offset: 0, dragging: true });
    };

    const onPointerMove = (e: PointerEvent) => {
      if (pointerStartY.current === null) return;
      if (pointerId.current !== null && e.pointerId !== pointerId.current) return;
      const dy = e.clientY - pointerStartY.current;
      const nextReady = indexRef.current < urlsRef.current.length - 1
        && loadedSetRef.current.has(indexRef.current + 1);
      // Same clamp as "no next reel ready" for the blocked-next case — the
      // stack shouldn't visually creep upward only to snap back once the
      // gesture can't actually go anywhere.
      const clampedDy = ((!nextReady || nextDisabledRef.current) && dy < 0) ? 0 : dy;
      pendingDragYRef.current = clampedDy;
      if (dragRafRef.current == null) {
        dragRafRef.current = requestAnimationFrame(flushDrag);
      }
    };

    const endDrag = (e: PointerEvent) => {
      if (pointerStartY.current === null) return;
      if (pointerId.current !== null && e.pointerId !== pointerId.current) return;
      // Cancel any pending coalesced flush — we're about to set the final
      // dragY=0 explicitly and don't want a stale dy to land after.
      if (dragRafRef.current != null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
      pendingDragYRef.current = null;

      const dy = e.clientY - pointerStartY.current;
      const h = containerRef.current?.clientHeight ?? 1;
      const threshold = h * snapThresholdRef.current;

      let nextIdx = indexRef.current;
      const nextReady = indexRef.current < urlsRef.current.length - 1
        && loadedSetRef.current.has(indexRef.current + 1);
      // A swipe past the "next game" threshold only advances when the next
      // game gesture isn't blocked (autoplay running / a modal open); it
      // otherwise just snaps back rather than falling through to open
      // history — an upward swipe was clearly not a request for history.
      if (dy < -threshold && nextReady && !nextDisabledRef.current) nextIdx = indexRef.current + 1;
      else if (dy > threshold) {
        setHistoryOpen(true);
        onHistoryOpenChangeRef.current?.(true);
      }

      if (nextIdx !== indexRef.current) {
        setIndex(nextIdx);
        onIndexChangeRef.current?.(nextIdx);
        for (let i = 0; i < nextIdx; i++) destroyIframe(i);
      }

      setDragY(0);
      setDragging(false);
      onDragChangeRef.current?.({ offset: 0, dragging: false });
      pointerStartY.current = null;
      pointerId.current = null;
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      if (dragRafRef.current != null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };
  }, [destroyIframe, flushDrag]);

  // Keep the stack clamped to valid range in case we lose a frame.
  useEffect(() => {
    if (index > urls.length - 1) setIndex(Math.max(0, urls.length - 1));
  }, [urls, index]);

  // Swipe-driven preload. Whenever the active index changes (i.e. the user
  // swiped to a new reel), kick off the next-ahead preload.
  // We skip index 0: at startup the initial preload is owned by the
  // `game.loaded` → `appendNext` path, and skipping here keeps this
  // effect safe against React StrictMode's double-invocation (which would
  // otherwise fire `appendNext` before the first game has loaded).
  useEffect(() => {
    if (index === 0) return;
    appendNext();
  }, [index, appendNext]);

  // Record a history entry whenever the user swipes forward. titlesRef /
  // urlsRef / spinsPerIndexRef are declared above (so destroyIframe can
  // prune them).
  const prevIndexRef = useRef(0);
  useEffect(() => {
    if (index > prevIndexRef.current) {
      const departed = prevIndexRef.current;
      setVisitedGames((prev) => {
        const next = [...prev, {
          gameTitle: titlesRef.current[departed] ?? `Game ${departed + 1}`,
          url: urlsRef.current[departed] ?? '',
          timestamp: Date.now(),
          spins: spinsPerIndexRef.current[departed] ?? 0,
        }];
        // Cap retention — a long session shouldn't grow this unbounded.
        return next.length > MAX_VISITED_GAMES
          ? next.slice(next.length - MAX_VISITED_GAMES)
          : next;
      });
    }
    prevIndexRef.current = index;
  }, [index]);

  // When a history item is clicked, close the history screen immediately
  // (rather than leaving it open behind a per-row spinner until the new
  // game finishes loading — a full-screen block-everything overlay with a
  // small spinner, below, takes over as the "something's happening" cue
  // instead), then tear down all live iframes and reset the stack to the
  // selected URL.
  const handleHistoryItemClick = useCallback((item: HistoryItem) => {
    setDismissProgress(0);
    setHistoryOpen(false);
    onHistoryOpenChangeRef.current?.(false);

    // Tapping the *current* game (the "Current" row at the bottom of the
    // panel) isn't actually navigating anywhere — the player is already
    // there. Just close history; reloading it from scratch would tear down
    // a perfectly live game and reset its spin count for no reason.
    if (item.url === urlsRef.current[indexRef.current]) return;

    // Send teardown to every iframe that hasn't already been destroyed.
    iframeRefs.current.forEach((iframe, i) => {
      if (!destroyedRef.current.has(i)) {
        try { iframe?.contentWindow?.postMessage({ type: destroyMessage }, '*'); } catch { /* noop */ }
      }
    });
    // Reset all stack state. Bump the generation so any pending destroyIframe
    // timeouts don't blank the newly-mounted iframe.
    destroyedRef.current = new Set();
    destroyGenRef.current += 1;
    spinsPerIndexRef.current = {};
    activatedAtRef.current = {};
    prevIndexRef.current = 0;
    setUrls([item.url]);
    setIndex(0);
    setLoadedSet(new Set());
    setTitles({});
    setUnmountedSet(new Set());
    setLoadingHistoryUrl(item.url);
    setLoadingHistoryTitle(item.gameTitle || null);
    setReloadKey((k) => k + 1);
  }, [destroyMessage]);

  // Ends the "loading from history" blocked window (see the full-screen
  // overlay below) once the newly selected game reports game.loaded.
  // History itself is already closed by this point — see
  // `handleHistoryItemClick` above — so this no longer needs to touch
  // `historyOpen`/`dismissProgress`.
  useEffect(() => {
    if (!loadingHistoryUrl) return;
    if (!loadedSet.has(0)) return;
    setLoadingHistoryUrl(null);
    setLoadingHistoryTitle(null);
  }, [loadedSet, loadingHistoryUrl]);

  // Report the status of the reel the user would swipe to next (index + 1).
  // 'none' when there is no queued reel, 'loading' while we're waiting for
  // its game.loaded signal, 'ready' once we've heard that signal.
  useEffect(() => {
    const nextIndex = index + 1;
    let status: 'none' | 'loading' | 'ready';
    if (nextIndex >= urls.length) status = 'none';
    else if (loadedSet.has(nextIndex)) status = 'ready';
    else status = 'loading';
    const progress = loadProgressMap[nextIndex] ?? 0;
    onNextLoadingChange?.({ status, nextIndex, progress });
  }, [index, urls, loadedSet, loadProgressMap, onNextLoadingChange]);

  // Tell off-screen games to stop rendering. We post to every iframe each
  // time index changes so late-loading frames also receive their initial
  // state. '*' target origin is necessary because every reel is cross-origin
  // (different port = different origin).
  //
  // `dragging` participates in the deps so we also re-send on drag start/end:
  // while a drag is in progress every iframe is paused (the user is sliding
  // two frames past each other — neither needs to spend GPU animating), and
  // on release the newly-active frame resumes. This keeps the swipe gesture
  // smooth on lower-end devices.
  //
  // `paused` lets the host (SlotUI) keep the active iframe paused while a
  // modal is open over it. We re-fire on every change of `paused` so that
  // any other dep change (e.g. `urls` from a preload) can't sneak a stray
  // resume past the modal.
  useEffect(() => {
    iframeRefs.current.forEach((el, i) => {
      const win = el?.contentWindow;
      if (!win) return;
      const shouldResume = !dragging && !paused && i === index;
      const msg = { type: shouldResume ? resumeMessage : pauseMessage };
      try {
        win.postMessage(msg, '*');
      } catch { /* iframe may not have navigated yet */ }
    });
  }, [index, urls, pauseMessage, resumeMessage, dragging, paused]);

  // When history is open the reel stack slides fully off-screen (down).
  // During a down-drag at index 0 the stack rubber-bands down, naturally
  // revealing the history panel that lives above it.
  const reelBaseOffset = -index * 100;
  // Only apply drag offset for upward swipes (negative dragY). Downward swipes
  // reveal the history overlay from the top; the reels should stay put.
  const reelDragOffset = dragging && dragY < 0 ? dragY : 0;
  const translate = `translateY(calc(${reelBaseOffset}% + ${reelDragOffset}px))`;

  // History overlay sits above the reel container and slides in from the top.
  const historyOverlayTransform = historyOpen
    ? `translateY(${dismissProgress}px)`
    : dragging && dragY > 0
      ? `translateY(calc(-100% + ${dragY}px))`
      : 'translateY(-100%)';
  const historyOverlayTransition = (dragging || dismissProgress < 0)
    ? 'none'
    : 'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)';
  // willChange promotes a layer to the GPU — keeping it on permanently
  // costs compositor memory even when nothing is moving. Set it only while
  // the user is actually dragging the stack or the history overlay is
  // visible/animating; revert to 'auto' so the layer can be reclaimed.
  const historyLayerActive = historyOpen || dragging || dismissProgress < 0;

  // Stable object identity across renders unless one of these actually
  // changed — this used to be a fresh `{ ..., timestamp: Date.now() }`
  // literal built inline in JSX on every render (including every drag
  // frame), which both defeated any memoization downstream and gave the
  // "currently playing" entry a constantly-ticking fake timestamp.
  const currentHistoryItem = useMemo(
    () => ({
      gameTitle: titles[index] ?? `Game ${index + 1}`,
      url: urls[index] ?? '',
      timestamp: getActivatedAt(index),
      spins: spinsPerIndexRef.current[index] ?? 0,
    }),
    // spinsPerIndexRef.current[index] is a ref read, not state — but it's
    // read fresh on every render here, so including it as a dep still
    // does the right thing: recompute when a render happens with a
    // different spin count, bail out (same reference) otherwise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [index, titles, urls, spinsPerIndexRef.current[index]],
  );

  return (
    <>
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <div
          ref={containerRef}
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            touchAction: 'none',
            userSelect: 'none',
            background: 'var(--surface-2)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              transform: translate,
              transition: dragging
                ? 'none'
                : 'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)',
              // Only hint the compositor while the layer is moving. Permanent
              // willChange keeps a backing surface allocated for every
              // iframe + the container; on iOS that adds up fast.
              willChange: dragging ? 'transform' : 'auto',
            }}
          >
            {urls.map((url, i) => {
              // Iframe lifecycle gate: once destroyIframe's unmount phase
              // fires, drop the element entirely so its document, WebGL
              // contexts, and any retained AudioContext go with it. Past
              // reels stayed in the DOM in the previous implementation,
              // which was the primary driver of iOS WebGL-context-cap
              // crashes after a handful of swipes.
              if (unmountedSet.has(i)) return null;
              // Only the active reel and the immediate next-prefetch need
              // to be alive at any given moment. Any older reel that's
              // still in the grace window between destroy postMessage and
              // unmount will fall into the destroyedRef branch below.
              const inActiveWindow = i >= index && i <= index + 1;
              const inGracePeriod = destroyedRef.current.has(i);
              if (!inActiveWindow && !inGracePeriod) return null;
              // Iframes that aren't active or queued for the next swipe
              // get visibility:hidden so the WebKit compositor stops
              // painting them. Many WebGL apps also use visibility/IO
              // signals to pause their render loop — extra savings.
              const visible = i === index || i === index + 1;
              return (
              <div
                key={url + ':' + i + (i === 0 ? ':' + reloadKey : '')}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${i * 100}%`,
                  height: '100%',
                  visibility: visible ? 'visible' : 'hidden',
                }}
              >
                <iframe
                  ref={(el) => {
                    iframeRefs.current[i] = el;
                    // Fires the instant this DOM node (and its `src`, i.e.
                    // the actual navigation) is created — see perfLog.ts's
                    // comment for why this is the mark that matters for the
                    // "big gap before the reel loads" investigation. Guard
                    // against logging twice for the same index (ref
                    // callbacks can re-fire on unrelated re-renders).
                    if (el && !loggedIframeIndicesRef.current.has(i)) {
                      loggedIframeIndicesRef.current.add(i);
                      perfLog(`iframe #${i} element created, src set (${url})`);
                    }
                  }}
                  src={url}
                  title={`Reels ${i + 1}`}
                  sandbox={sandbox}
                  allow={safeAllow}
                  onLoad={(e) => {
                    perfLog(`iframe #${i} onLoad fired`);
                    const win = (e.currentTarget as HTMLIFrameElement).contentWindow;
                    if (!win) return;
                    const isActive = i === index && !paused;
                    const msg = { type: isActive ? resumeMessage : pauseMessage };
                    try { win.postMessage(msg, '*'); } catch { /* noop */ }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 0,
                    display: 'block',
                    background: 'var(--surface-2)',
                    // Only the active frame ever gets direct pointer
                    // input, and only while `interactive` is on — see
                    // the prop doc for why this defaults to 'none'.
                    pointerEvents: i === index && interactive ? 'auto' : 'none',
                  }}
                />
              </div>
              );
            })}
          </div>
        </div>
      </div>

      {createPortal(
        <div
          className="reelay-slot"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            transform: historyOverlayTransform,
            transition: historyOverlayTransition,
            // Same dynamic-willChange treatment as the reel stack: only
            // promote a layer while we're actually animating.
            willChange: historyLayerActive ? 'transform' : 'auto',
            pointerEvents: historyOpen ? 'auto' : 'none',
          }}
        >
          <HistoryPanel
            items={visitedGames}
            currentItem={currentHistoryItem}
            loadingUrl={loadingHistoryUrl}
            onItemClick={handleHistoryItemClick}
            onClose={handleCloseHistory}
            onDismissProgress={setDismissProgress}
          />
        </div>,
        document.body,
      )}

      {/* Full-screen block-everything overlay while a history-selected game
          is loading. Portalled to document.body (same trick as the
          HistoryPanel above) specifically so `pointerEvents: 'auto'` here
          covers the shell's own chrome — bet/auto/spin buttons live in
          SlotUI, a sibling tree entirely outside ReelsStack's own stacking
          context, so nothing rendered inside ReelsStack's normal JSX could
          ever sit above them. A higher z-index than the HistoryPanel
          portal (9999) so it also covers the panel itself during the
          instant it's animating closed.
          Background is the same two gradient layers Preloader.tsx uses
          (base radial-gradient + dark radial overlay) rather than staying
          transparent — the newly (re)mounted game iframe underneath briefly
          shows its *own* loading screen while it boots, and a transparent
          overlay let that flash through beneath the spinner. Opaque and
          full-size, it fully hides that instead of just blocking clicks. */}
      {loadingHistoryUrl && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto',
            background: 'radial-gradient(224.88% 76.67% at -44.53% 100%, #FE47BA 35.58%, #FE37B4 44.23%, #630426 94.23%)',
          }}
        >
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(977.1% 186.8% at 31.09% 0%, rgba(9,4,24,0) 0%, #090418 100%)',
            }}
          />
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <svg className="reelay-spinner" width="28" height="28" viewBox="0 0 28 28" aria-hidden>
              <circle cx="14" cy="14" r="11" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
              <path d="M14 3 a11 11 0 0 1 11 11" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
            </svg>
            {loadingHistoryTitle && (
              <span
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  color: '#fff',
                  textShadow: '0 1px 6px rgba(0,0,0,0.5)',
                  textAlign: 'center',
                  maxWidth: '70vw',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {loadingHistoryTitle}
              </span>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
});
